/* eslint-disable @typescript-eslint/no-unused-vars */
import { Card, CardBody, Col, Container, Input, Label, Row, Button } from 'reactstrap'
import DataTable, { TableColumn } from "react-data-table-component";
import { useEffect, useMemo, useState } from 'react';
import * as XLSX from 'xlsx';
import { SearchTableButton } from '../../../../utils/Constant';
import api from '../../../../utils/api';
import { BookingHistoryTableDataColumnType } from '../../../../Types/Application/Ecommerce/Ecommerce';
import Swal from 'sweetalert2';
import BookingHistoryFilterHeader from '../../../../Componant/Application/Ecommerce/ProductList/BookingHistoryFilterHeader';
import { BookingHistoryTableAction, BookingHistoryTableProductName, BookingHistoryTableStatus } from '../../../../Data/Application/Ecommerce/BookingHistory';
import { useNavigate } from 'react-router-dom';

// ── Swal custom styles injected once ─────────────────────────────────────────
const SWAL_STYLES = `
  .swal-booking-edit .swal2-popup {
    border-radius: 18px;
    padding: 0;
    overflow: hidden;
    width: 540px;
    font-family: 'Segoe UI', sans-serif;
    box-shadow: 0 24px 64px rgba(0,0,0,0.18);
  }
  .swal-booking-edit .swal2-title {
    background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%);
    color: #fff;
    padding: 20px 28px 18px;
    font-size: 18px;
    font-weight: 600;
    letter-spacing: 0.3px;
    text-align: left;
    margin: 0;
  }
  .swal-booking-edit .swal2-html-container {
    padding: 20px 24px 8px;
    margin: 0;
    overflow: visible;
  }
  .swal-booking-edit .swal2-actions {
    padding: 12px 24px 22px;
    gap: 10px;
    justify-content: flex-end;
  }
  .swal-booking-edit .swal2-confirm {
    background: linear-gradient(135deg, #667eea, #764ba2) !important;
    border-radius: 10px !important;
    padding: 10px 26px !important;
    font-weight: 600 !important;
    font-size: 14px !important;
    box-shadow: 0 4px 14px rgba(102,126,234,0.4) !important;
    transition: opacity 0.2s !important;
  }
  .swal-booking-edit .swal2-confirm:hover { opacity: 0.88 !important; }
  .swal-booking-edit .swal2-cancel {
    background: #f1f3f5 !important;
    color: #555 !important;
    border-radius: 10px !important;
    padding: 10px 22px !important;
    font-weight: 500 !important;
    font-size: 14px !important;
  }
  /* grid layout for edit fields */
  .edit-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 12px 16px;
  }
  .edit-grid .full-width { grid-column: 1 / -1; }
  .edit-field {
    display: flex;
    flex-direction: column;
    gap: 5px;
    text-align: left;
  }
  .edit-field label {
    font-size: 11px;
    font-weight: 600;
    color: #888;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }
  .edit-field input,
  .edit-field select {
    border: 1.5px solid #e2e6ea;
    border-radius: 9px;
    padding: 9px 12px;
    font-size: 13.5px;
    color: #2d3748;
    background: #f8f9fc;
    transition: border-color 0.2s, box-shadow 0.2s;
    outline: none;
    width: 100%;
    box-sizing: border-box;
    -webkit-appearance: none;
    appearance: none;
  }
  .edit-field input:focus,
  .edit-field select:focus {
    border-color: #667eea;
    box-shadow: 0 0 0 3px rgba(102,126,234,0.12);
    background: #fff;
  }
  .select-wrapper {
    position: relative;
  }
  .select-wrapper::after {
    content: '';
    position: absolute;
    right: 12px;
    top: 50%;
    transform: translateY(-50%);
    width: 0;
    height: 0;
    border-left: 5px solid transparent;
    border-right: 5px solid transparent;
    border-top: 6px solid #888;
    pointer-events: none;
  }
  .select-wrapper select { padding-right: 32px; }
  .section-divider {
    grid-column: 1 / -1;
    display: flex;
    align-items: center;
    gap: 10px;
    margin: 4px 0 2px;
  }
  .section-divider span {
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.8px;
    color: #aaa;
    white-space: nowrap;
  }
  .section-divider::before, .section-divider::after {
    content: '';
    flex: 1;
    height: 1px;
    background: #eee;
  }
`;

interface BookingUpdateFormData {
  customerName: string;
  customerMobileNumber: string;
  address: string;
  bookingDate: string;
  numberOdAdults: string;
  numberOfChildren: string;
  portal: string | number;
  ticketStatus: string | number;
}

// ── Status badge helper ───────────────────────────────────────────────────────
const StatusBadge = ({ value }: { value: string }) => {
  const isUsed = value === "Used";
  return (
    <span style={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 5,
      padding: '3px 10px',
      borderRadius: 20,
      fontSize: 11,
      fontWeight: 600,
      letterSpacing: '0.3px',
      background: isUsed ? '#d4edda' : '#fff3cd',
      color: isUsed ? '#1a5c2a' : '#856404',
      border: `1px solid ${isUsed ? '#b8ddc4' : '#ffeaa0'}`,
    }}>
      <span style={{
        width: 6, height: 6, borderRadius: '50%',
        background: isUsed ? '#28a745' : '#ffc107',
        display: 'inline-block',
      }} />
      {value}
    </span>
  );
};

const PortalBadge = ({ value }: { value: string }) => (
  <span style={{
    display: 'inline-flex',
    alignItems: 'center',
    padding: '3px 10px',
    borderRadius: 20,
    fontSize: 11,
    fontWeight: 600,
    background: value === 'Website' ? '#e3f0ff' : '#f0e6ff',
    color: value === 'Website' ? '#1a4fa0' : '#5a1a9c',
    border: `1px solid ${value === 'Website' ? '#b8d4f7' : '#d4b8f7'}`,
  }}>
    {value}
  </span>
);

// ── Main component ────────────────────────────────────────────────────────────
const BookingHistoryContainer = () => {
  const [filterText, setFilterText] = useState("");
  const [bookingHistoryTableDatas, setBookingHistoryTableDatas] = useState([]);
  const navigate = useNavigate();

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalRows, setTotalRows] = useState(0);
  const [loading, setLoading] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  // Get user data
  const userData = (() => {
    try { return JSON.parse(localStorage.getItem("user") || '{}'); } catch { return {}; }
  })();
  const role = userData.role;
  const staffId = userData.staffId;

  // Inject Swal styles once
  useEffect(() => {
    if (!document.getElementById('swal-booking-styles')) {
      const style = document.createElement('style');
      style.id = 'swal-booking-styles';
      style.textContent = SWAL_STYLES;
      document.head.appendChild(style);
    }
  }, []);

  const fetchTickets = async (page: number, size: number, startDate?: string, endDate?: string) => {
    try {
      setLoading(true);
      let response;

      if (role === "Admin") {
        if (startDate) {
          // Use filtered API when dates are provided
          response = await api.get(
            `/api/TicketBooking/GetTicketBookingFiltered?startDate=${startDate}&endDate=${endDate}&pageNumber=${page}&pageSize=${size}`
          );
        } else {
          // Use regular pagination API
          response = await api.get(
            `/api/TicketBooking/GetTicketBookingPagination?pageNumber=${page}&pageSize=${size}`
          );
        }
        if (response.status === 200) {
          setBookingHistoryTableDatas(
            response.data.items.map((item: any): BookingHistoryTableDataColumnType => ({
              ticketBookingId: item.ticketBookingId,
              ticketNumber: item.ticketNumber,
              ticketCountNo: item?.ticketCountNo ?? 'SWP-XX',
              emailId: item.emailId,
              ticketUniqueId: item.ticketUniqueId,
              bookingDate: item.bookingDate ? item.bookingDate.split('T')[0] : '',
              customerName: item.customerName,
              customerMobileNumber: item.customerMobileNumber,
              address: item.address,
              numberOdAdults: item.numberOdAdults,
              numberOfChildren: item.numberOfChildren,
              createdDate: item.createdDate ? item.createdDate.split('T')[0] : '',
              applicationUserId: item.applicationUserId,
              staffName: item.staffName,
              staffId: item.staffId,
              counterStatus: item.counterStatus,
              payAmount: item.payAmount,
              razorpayPaymentId: item.razorpayPaymentId,
              ticketStatus: item.ticketStatus == 0 ? "Not Used" : "Used",
              portal: item.portal,
              onlineAmount: item?.onlineAmount,
              cashAmount: item?.cashAmount,
              discountPer: item?.discountPer,
              discountAmt: item?.discountAmt,
            }))
          );
          setTotalRows(response.data.totalCount);
        }
      } else {
        response = await api.get(
          `/api/TicketBooking/GetAllBookingHistoryByStaffId?staffId=${staffId}`
        );
        if (response.status === 200) {
          setBookingHistoryTableDatas(
            response.data.map((item: any): BookingHistoryTableDataColumnType => ({
              ticketBookingId: item.ticketBookingId,
              ticketNumber: item.ticketNumber,
              ticketCountNo: item?.ticketCountNo ?? 'SWP-XX',
              emailId: item.emailId,
              ticketUniqueId: item.ticketUniqueId,
              bookingDate: item.bookingDate ? item.bookingDate.split('T')[0] : '',
              customerName: item.customerName,
              customerMobileNumber: item.customerMobileNumber,
              address: item.address,
              numberOdAdults: item.numberOdAdults,
              numberOfChildren: item.numberOfChildren,
              createdDate: item.createdDate ? item.createdDate.split('T')[0] : '',
              applicationUserId: item.applicationUserId,
              staffName: item.staffName,
              staffId: item.staffId,
              counterStatus: item.counterStatus,
              payAmount: item.payAmount,
              razorpayPaymentId: item.razorpayPaymentId,
              ticketStatus: item.ticketStatus == 0 ? "Not Used" : "Used",
              portal: item.portal,
              onlineAmount: item?.onlineAmount,
              cashAmount: item?.cashAmount,
              discountPer: item?.discountPer,
              discountAmt: item?.discountAmt,
            }))
          );
          setTotalRows(response.data.length);
        }
      }
    } catch (error) {
      console.error("Failed to fetch booking history:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (startDate) {
      fetchTickets(currentPage, pageSize, startDate, endDate);
    } else {
      fetchTickets(currentPage, pageSize);
    }
  }, [currentPage, pageSize, startDate, endDate]);

  const handleDateSearch = () => {
    if (startDate) {
      setCurrentPage(1);
      fetchTickets(1, pageSize, startDate, endDate);
    } else {
      // If no dates selected, fetch all data
      setCurrentPage(1);
      fetchTickets(1, pageSize);
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await api.put(`/api/TicketBooking/TicketBookingCancel?ticketBookingId=${id}`);
      Swal.fire('Cancelled!', 'Ticket Booking has been cancelled successfully.', 'success');
      await fetchTickets(currentPage, pageSize, startDate, endDate);
    } catch (error) {
      Swal.fire('Error!', 'Failed to cancel the ticket booking.', 'error');
    }
  };

  const handleUpdate = async (id: number, updatedData: BookingUpdateFormData) => {
    try {
      const payload = {
        ticketBookingId: id,
        bookingDate: updatedData.bookingDate
          ? new Date(updatedData.bookingDate).toISOString()
          : new Date().toISOString(),
        customerName: updatedData.customerName,
        customerMobileNumber: updatedData.customerMobileNumber,
        address: updatedData.address,
        numberOdAdults: String(updatedData.numberOdAdults),
        numberOfChildren: String(updatedData.numberOfChildren),
        createdDate: new Date().toISOString(),
        // ✅ Convert human-readable labels back to numeric values for API
        portal: updatedData.portal === "Website" ? 0 : 1,
        ticketStatus: updatedData.ticketStatus === "Used" ? 1 : 0,
      };
      await api.put(`/api/TicketBooking/UpdateTicketBooking`, payload);
      Swal.fire('Updated!', 'Ticket Booking has been updated successfully.', 'success');
      await fetchTickets(currentPage, pageSize, startDate, endDate);
    } catch (error) {
      Swal.fire('Error!', 'Failed to update the ticket booking.', 'error');
    }
  };

  // ── Edit modal with proper dropdowns ─────────────────────────────────────
  const openEditModal = (row: BookingHistoryTableDataColumnType) => {
    Swal.fire({
      title: `✏️ Edit Booking — ${row.ticketNumber}`,
      customClass: { popup: 'swal-booking-edit' },
      html: `
        <div class="edit-grid">

          <div class="section-divider"><span>Customer Info</span></div>

          <div class="edit-field">
            <label>Customer Name</label>
            <input id="swal-customerName" placeholder="Customer Name" value="${row.customerName ?? ''}" disabled/>
          </div>
          <div class="edit-field">
            <label>Mobile Number</label>
            <input id="swal-customerMobileNumber" placeholder="Mobile Number" value="${row.customerMobileNumber ?? ''}" disabled/>
          </div>
          <div class="edit-field full-width">
            <label>Address</label>
            <input id="swal-address" placeholder="Address" value="${(row.address ?? '').replace(/"/g, '&quot;')}" disabled/>
          </div>

          <div class="section-divider"><span>Booking Details</span></div>

          <div class="edit-field">
            <label>Booking Date</label>
            <input id="swal-bookingDate" type="date" value="${row.bookingDate ?? ''}" disabled/>
          </div>
          <div class="edit-field">
            <label>No. of Adults</label>
            <input id="swal-numberOdAdults" type="number" min="0" placeholder="Adults" value="${row.numberOdAdults ?? 0}" disabled/>
          </div>
          <div class="edit-field">
            <label>No. of Children</label>
            <input id="swal-numberOfChildren" type="number" min="0" placeholder="Children" value="${row.numberOfChildren ?? 0}" disabled/>
          </div>

          <div class="section-divider"><span>Status</span></div>

          <div class="edit-field">
            <label>Ticket Status</label>
            <div class="select-wrapper">
              <select id="swal-ticketStatus">
                <option value="Not Used" ${row.ticketStatus === 'Not Used' ? 'selected' : ''}>Not Used</option>
                <option value="Used"     ${row.ticketStatus === 'Used'     ? 'selected' : ''}>Used</option>
              </select>
            </div>
          </div>
          <div class="edit-field d-none">
            <label>Portal</label>
            <div class="select-wrapper">
              <select id="swal-portal">
                <option value="Website"  ${row.portal === 'Website'  ? 'selected' : ''}>Website</option>
                <option value="Software" ${row.portal === 'Software' ? 'selected' : ''}>Software</option>
              </select>
            </div>
          </div>

        </div>
      `,
      showCancelButton: true,
      confirmButtonText: 'Save Changes',
      cancelButtonText: 'Cancel',
      focusConfirm: false,
      preConfirm: (): BookingUpdateFormData => ({
        customerName:         (document.getElementById('swal-customerName')         as HTMLInputElement).value,
        customerMobileNumber: (document.getElementById('swal-customerMobileNumber') as HTMLInputElement).value,
        address:              (document.getElementById('swal-address')              as HTMLInputElement).value,
        bookingDate:          (document.getElementById('swal-bookingDate')          as HTMLInputElement).value,
        numberOdAdults:       (document.getElementById('swal-numberOdAdults')       as HTMLInputElement).value,
        numberOfChildren:     (document.getElementById('swal-numberOfChildren')     as HTMLInputElement).value,
        ticketStatus:         (document.getElementById('swal-ticketStatus')         as HTMLSelectElement).value,
        portal:               (document.getElementById('swal-portal')               as HTMLSelectElement).value,
      }),
    }).then((result) => {
      if (result.isConfirmed && result.value) {
        handleUpdate(row.ticketBookingId, result.value as BookingUpdateFormData);
      }
    });
  };

  // ── Table columns ─────────────────────────────────────────────────────────
  const bookingHistoryTableDataColumn: TableColumn<BookingHistoryTableDataColumnType>[] =
    [
      {
        name: "Ticket ID",
        cell: (row) => (
          <BookingHistoryTableProductName title={row.ticketCountNo} />
        ),
        grow: 2,
      },
      {
        name: "Ticket Number",
        selector: (row) => `${row.ticketUniqueId}`,
        grow: 2,
      },
      {
        name: "Name",
        selector: (row) => `${row.customerName}`,
        minWidth: "160px",
      },
      {
        name: "Mobile No.",
        cell: (row) => (
          <BookingHistoryTableStatus title={row.customerMobileNumber} />
        ),
      },
      {
        name: "Adults",
        cell: (row) => <BookingHistoryTableStatus title={row.numberOdAdults} />,
        center: true,
      },
      {
        name: "Children",
        cell: (row) => (
          <BookingHistoryTableStatus title={row.numberOfChildren} />
        ),
        center: true,
      },
      {
        name: "Discount(%)",
        cell: (row) => (
          <span style={{ fontWeight: 600, color: "#1a5c2a" }}>
            {row.discountPer || 0}
          </span>
        ),
      },
      {
        name: "Discount Amount",
        cell: (row) => (
          <span style={{ fontWeight: 600, color: "#1a5c2a" }}>
            ₹{row.discountAmt || 0}
          </span>
        ),
        minWidth: "160px",
      },
      {
        name: "Pay Amount",
        cell: (row) => (
          <span style={{ fontWeight: 600, color: "#1a5c2a" }}>
            ₹{row.payAmount || 0}
          </span>
        ),
      },
      {
        name: "Online Amount",
        cell: (row) => (
          <span style={{ fontWeight: 600, color: "#1a5c2a", width: "220px", display: "inline-block" }}>
            ₹{row.onlineAmount || 0}
          </span>
        ),
      },
      {
        name: "Cash Amount",
        cell: (row) => (
          <span style={{ fontWeight: 600, color: "#1a5c2a" }}>
            ₹{row.cashAmount || 0}
          </span>
        ),
      },
      {
        name: "Razorpay PaymentId",
        cell: (row) => (
          <span>{row.razorpayPaymentId || "Booked By Software"}</span>
        ),
        minWidth: "160px",
      },
      {
        name: "Booking Date",
        cell: (row) => <BookingHistoryTableStatus title={row.bookingDate} />,
      },
      {
        name: "Address",
        cell: (row) => (
          <div
            style={{
              whiteSpace: "normal",
              wordBreak: "break-word",
              width: "220px",
              fontSize: 12,
            }}
          >
            {row.address}
          </div>
        ),
        wrap: true,
      },
      {
        name: "Counter",
        cell: (row) => <BookingHistoryTableStatus title={row.counterStatus} />,
      },
      {
        name: "Ticket Status",
        cell: (row) => <StatusBadge value={row.ticketStatus as string} />,
      },
      {
        name: "Portal",
        cell: (row) => <PortalBadge value={row.portal as string} />,
      },
      {
        name: "Created Date",
        cell: (row) => <BookingHistoryTableStatus title={row.createdDate} />,
      },
      {
        name: "Action",
        cell: (row) => (
          <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
            {/* Print Invoice */}
            <button
              title="Print Invoice"
              onClick={() =>
                navigate(`/ticket-booking-invoice/${row.ticketBookingId}`)
              }
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                width: 32,
                height: 32,
                border: "none",
                borderRadius: 8,
                cursor: "pointer",
                background: "linear-gradient(135deg,#ff4d6d,#c9184a)",
                color: "#fff",
                boxShadow: "0 2px 6px rgba(255,77,109,0.35)",
                transition: "opacity 0.2s",
              }}
              onMouseEnter={(e) => (e.currentTarget.style.opacity = "0.8")}
              onMouseLeave={(e) => (e.currentTarget.style.opacity = "1")}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="15"
                height="15"
                fill="currentColor"
                viewBox="0 0 24 24"
              >
                <path d="M19 8V6a2 2 0 0 0-2-2H7a2 2 0 0 0-2 2v2H3v8h4v4h10v-4h4V8h-2zm-2 10H7v-4h10v4zm2-6a1 1 0 1 1-2 0 1 1 0 0 1 2 0zm-2-4H7V6h10v2z" />
              </svg>
            </button>

            {/* Edit */}
            {/* <button
            title="Edit Booking"
            onClick={() => openEditModal(row)}
            style={{
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              width: 32, height: 32, border: 'none', borderRadius: 8, cursor: 'pointer',
              background: 'linear-gradient(135deg,#667eea,#764ba2)',
              color: '#fff', boxShadow: '0 2px 6px rgba(102,126,234,0.38)',
              transition: 'opacity 0.2s',
            }}
            onMouseEnter={e => (e.currentTarget.style.opacity = '0.8')}
            onMouseLeave={e => (e.currentTarget.style.opacity = '1')}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" fill="currentColor" viewBox="0 0 24 24">
              <path d="M3 17.25V21h3.75l11.06-11.06-3.75-3.75L3 17.25zM20.71 7.04a1 1 0 0 0 0-1.41l-2.34-2.34a1 1 0 0 0-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"/>
            </svg>
          </button> */}

            {row.ticketStatus !== 1 && (
              <button
  title="Edit Booking"
  onClick={() => openEditModal(row)}
  disabled={row.ticketStatus === "Used"}   // ✅ yaha condition
  style={{
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: 32,
    height: 32,
    border: "none",
    borderRadius: 8,
    cursor: row.ticketStatus === "Used" ? "not-allowed" : "pointer", // 👈 UX
    background: row.ticketStatus === "Used"
      ? "#ccc"
      : "linear-gradient(135deg,#667eea,#764ba2)",
    color: "#fff",
    boxShadow: "0 2px 6px rgba(102,126,234,0.38)",
    opacity: row.ticketStatus === "Used" ? 0.6 : 1, // 👈 faded look
    transition: "opacity 0.2s",
  }}
  onMouseEnter={e => (e.currentTarget.style.opacity = "0.8")}
  onMouseLeave={e => (e.currentTarget.style.opacity = "1")}
>
  <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" fill="currentColor" viewBox="0 0 24 24">
    <path d="M3 17.25V21h3.75l11.06-11.06-3.75-3.75L3 17.25zM20.71 7.04a1 1 0 0 0 0-1.41l-2.34-2.34a1 1 0 0 0-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"/>
  </svg>
</button>
            )}

            {/* Cancel */}
            <BookingHistoryTableAction
              ticketBookingId={row.ticketBookingId}
              onDelete={handleDelete}
            />
          </div>
        ),
        minWidth: "150px",
      },
    ];

  const handlePageChange = (page: number) => setCurrentPage(page);
  const handlePerRowsChange = (newPerPage: number, page: number) => {
    setPageSize(newPerPage);
    setCurrentPage(page);
  };

  const filteredItems = bookingHistoryTableDatas.filter((item: any) => {
    if (!filterText) return true;
    const search = filterText.toLowerCase();
    return Object.values(item).some((value) =>
      value?.toString().toLowerCase().includes(search)
    );
  });

  const exportToExcel = () => {
    const rowsToExport = filteredItems.map((item: any) => ({
      'Ticket ID': item.ticketCountNo,
      'Ticket Number': item.ticketUniqueId,
      'Name': item.customerName,
      'Mobile No.': item.customerMobileNumber,
      Adults: item.numberOdAdults,
      Children: item.numberOfChildren,
      'Discount Percentage': item.discountPer,
      'Discount Amount': item.discountAmt,
      'Pay Amount': item.payAmount,
      'Online Amount': item.onlineAmount,
      'Cash Amount': item.cashAmount,
      'Razorpay PaymentId': item.razorpayPaymentId,
      'Booking Date': item.bookingDate,
      Address: item.address,
      Counter: item.counterStatus,
      'Ticket Status': item.ticketStatus,
      Portal: item.portal,
      'Created Date': item.createdDate,
    }));

    const worksheet = XLSX.utils.json_to_sheet(rowsToExport);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'BookingHistory');
    XLSX.writeFile(workbook, 'BookingHistory.xlsx');
  };

  const subHeaderComponentMemo = useMemo(() => (
    <div>
      {/* Date Filter Section */}
      <div className="d-flex align-items-center gap-3 mb-3">
        <div className="d-flex align-items-center">
          <Label className="me-2">Start Date:</Label>
          <Input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            style={{ width: '150px' }}
          />
        </div>
        <div className="d-flex align-items-center">
          <Label className="me-2">End Date:</Label>
          <Input
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            style={{ width: '150px' }}
          />
        </div>
        <Button color="primary" size="sm" onClick={handleDateSearch}>
          Search
        </Button>
      { userData.role === "Admin" && (
        <Button color="success" size="sm" onClick={exportToExcel}>
          Download Excel
        </Button>
      )}
      </div>
      <div className="dataTables_filter">
        <div className="d-flex align-items-center">
          <Label className="me-2">{SearchTableButton}:</Label>
          <Input
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              setFilterText(e.target.value);
              setCurrentPage(1);
            }}
            type="search"
            value={filterText}
            style={{ minWidth: 250 }}
          />
        </div>
      </div>
    </div>
  ), [filterText, filteredItems, startDate, endDate]);

  const totalDisplayRows = filterText ? filteredItems.length : totalRows;

  return (
    <Container fluid>
      <Row>
        <Col sm="12">
          <Card>
            <CardBody>
              <div className="list-product-header">
                <BookingHistoryFilterHeader />
              </div>
              <div className="list-product">
                <div className="table-responsive">
                  <DataTable
                    data={filteredItems}
                    columns={bookingHistoryTableDataColumn}
                    striped
                    highlightOnHover
                    pagination
                    paginationServer
                    paginationTotalRows={totalDisplayRows}
                    onChangePage={handlePageChange}
                    onChangeRowsPerPage={handlePerRowsChange}
                    paginationDefaultPage={currentPage}
                    paginationPerPage={pageSize}
                    paginationRowsPerPageOptions={[10, 15, 20, 25, 30, 50, 100]}
                    progressPending={loading}
                    selectableRows
                    subHeader
                    subHeaderComponent={subHeaderComponentMemo}
                  />
                </div>
              </div>
            </CardBody>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default BookingHistoryContainer;